import { useCallback, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Calendar, Money, Person, Receipt, Suitcase } from '@factorialco/f0-react/icons/app'
import {
  DealDetailFragment,
  DealsPipelineViewDocument,
  type DealsPipelineViewQuery,
  type FinanceValueObjectsDealStageEnum,
  UpdateFinancialDealDocument,
} from 'generated/resources/graphql'
import { useWindowSize } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { MapVariablesParams } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import {
  dealStageAsValue,
  dealStatusAsVariant,
  dealTranslateStatus,
  getDealStageOptions,
} from 'modules/finance/components/Deals/utils'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

const DEFAULT_PER_PAGE = 10

export interface DealsPipelineViewProps {
  locale: string
  onClickCreateDeal?: (stage?: FinanceValueObjectsDealStageEnum) => void
  onChangeStage?: (dealId: string, newStage: FinanceValueObjectsDealStageEnum) => void
  clientId?: number
  showClientColumn?: boolean
}

export const DealsPipelineView = ({
  locale,
  onClickCreateDeal,
  onChangeStage,
  clientId,
  showClientColumn = true,
}: DealsPipelineViewProps) => {
  const { navigation } = useNavigation()
  const size = useWindowSize()
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableHeight, setAvailableHeight] = useState<number>()

  useEffect(() => {
    setAvailableHeight(containerRef.current?.parentElement?.offsetHeight)
  }, [size.height])

  const dealStageOptions = useMemo(() => getDealStageOptions(), [])

  const collectionFilters = useMemo(
    () =>
      ({
        stages: {
          type: 'in',
          label: i18n.t('finance.deals.columns.stage'),
          options: {
            options: dealStageOptions.map((dealStage) => ({
              value: dealStage.value,
              label: dealStage.label,
            })),
          },
        },
      } as const),
    [dealStageOptions]
  )

  const sourceLanes = useMemo(
    () =>
      dealStageOptions.map((dealStage) => ({
        id: dealStage.value,
        filters: { stages: [dealStage.value] },
      })),
    [dealStageOptions]
  )

  const [updateDeal] = useMutation(UpdateFinancialDealDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceDeal,
    ignoreResults: true,
    awaitRefetchQueries: false,
    onQueryUpdated: () => false,
  })

  const updateStage = useCallback<
    (
      dealToUpdate: DealDetailFragment,
      newStage: FinanceValueObjectsDealStageEnum
    ) => Promise<DealDetailFragment>
  >(
    async (dealToUpdate, newStage) => {
      const result = await updateDeal({
        variables: {
          id: dealToUpdate.id,
          name: dealToUpdate.name,
          clientId: Number(dealToUpdate.client?.id),
          estimatedAmountCents: dealToUpdate.estimatedAmountCents,
          currency: dealToUpdate.currency,
          stage: newStage,
          ownerId: Number(dealToUpdate.owner?.id),
          expectedCloseDate:
            dealToUpdate.expectedCloseDate?.trim() !== ''
              ? dealToUpdate.expectedCloseDate
              : undefined,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        app.setFlash({
          type: 'wrong',
          message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
        })
      })

      if (handled.success && handled.data.deal) {
        return handled.data.deal
      }

      throw new Error('Failed to update deal stage')
    },
    [updateDeal]
  )

  const mapData = useCallback(
    (data: DealsPipelineViewQuery) => data.finance.dealsConnection.nodes,
    []
  )

  const search = useMemo(() => ({ enabled: true } as const), [])

  const primaryActions = useMemo(
    () =>
      onClickCreateDeal
        ? () => ({
            label: i18n.t('finance.deals.actions.create'),
            icon: Add,
            onClick: () => onClickCreateDeal?.('new'),
          })
        : undefined,
    [onClickCreateDeal]
  )

  const itemUrl = useCallback(
    (item: DealDetailFragment) =>
      navigation.finance.sales.deals.dealId.details.fullPath({ dealId: item.id }),
    [navigation]
  )

  const mapVariables = useCallback(
    ({
      search: searchValue,
      filters,
      pagination,
      sortings,
    }: MapVariablesParams<typeof collectionFilters>) => {
      const perPage = pagination?.perPage ?? DEFAULT_PER_PAGE
      const cursorNum = pagination?.cursor ? Number(pagination.cursor) : 0
      const page = Math.floor(cursorNum / perPage) + 1

      return {
        search: searchValue,
        page,
        stages: filters.stages,
        sortOrder: sortings?.[0],
        clientIds: clientId ? [clientId] : undefined,
      }
    },
    [clientId]
  )

  const mapPagination = useCallback(
    (data: DealsPipelineViewQuery) => ({
      total: data.finance.dealsConnection.totalCount ?? 0,
      cursor: data.finance.dealsConnection.pageInfo.endCursor ?? '',
      hasNextPage: data.finance.dealsConnection.pageInfo.hasNextPage ?? false,
    }),
    []
  )

  const source = useGraphqlDataCollectionSource({
    filters: collectionFilters,
    lanes: sourceLanes,
    paginationType: 'infinite-scroll',
    document: DealsPipelineViewDocument,
    totalItemSummary: (total) => i18n.tp('finance.deals.total_items', { count: total }),
    mapData,
    search,
    primaryActions,
    itemUrl,
    mapVariables,
    mapPagination,
  })

  const columns = useMemo(
    () => [
      {
        label: i18n.t('finance.deals.columns.name'),
        render: (item: DealDetailFragment) => item.name || undefined,
      },
      {
        label: i18n.t('finance.deals.columns.stage'),
        render: (item: DealDetailFragment) => ({
          type: 'status' as const,
          value: {
            status: dealStatusAsVariant(item.stage),
            label: dealTranslateStatus(item.stage),
          },
        }),
      },
      {
        label: i18n.t('finance.deals.columns.estimated_amount'),
        render: (item: DealDetailFragment) =>
          formattedAmount({
            amountCents: Number(item.estimatedAmountCents),
            currency: item.currency,
            locale,
          }),
      },
      ...(showClientColumn
        ? [
            {
              label: i18n.t('finance.deals.columns.client'),
              render: (item: DealDetailFragment) => contactFullName(item.client),
            },
          ]
        : []),
      {
        label: i18n.t('finance.deals.columns.owner'),
        render: (item: DealDetailFragment) => item.owner?.fullName || undefined,
      },
      {
        label: i18n.t('finance.deals.columns.expected_close_date'),
        render: (item: DealDetailFragment) =>
          item.expectedCloseDate
            ? convertDate(item.expectedCloseDate, 'backend_datetime', 'ui_dmy_long')
            : i18n.t('finance.deals.not_applicable'),
      },
    ],
    [locale, showClientColumn]
  )

  const lanes = useMemo(
    () =>
      dealStageOptions.map((dealStage) => ({
        id: dealStage.value,
        title: dealStage.label,
        variant: dealStatusAsVariant(dealStage.value),
      })),
    [dealStageOptions]
  )

  const handleCreate = useUnsafeCallback(async (newStage: string) => {
    const stage = dealStageOptions.find((stage) => stage.value === newStage)
    if (stage) {
      onClickCreateDeal?.(stage.value)
    } else {
      onClickCreateDeal?.()
    }
  }, [])

  const handleMove = useCallback(
    async (_fromLaneId: string, toLaneId: string, sourceRecord: DealDetailFragment) => {
      const newStage = dealStageAsValue(toLaneId)
      const updatedDeal = await updateStage(sourceRecord, newStage)
      onChangeStage?.(String(updatedDeal.id), newStage)
      return updatedDeal
    },
    [updateStage, onChangeStage]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'kanban' as const,
        options: {
          lanes,
          onCreate: !onClickCreateDeal ? undefined : handleCreate,
          onMove: handleMove,
          title: (deal: DealDetailFragment) => deal.name,
          metadata: (deal: DealDetailFragment) => [
            {
              icon: Money,
              property: {
                type: 'text' as const,
                value: formattedAmount({
                  amountCents: Number(deal.estimatedAmountCents),
                  currency: deal.currency,
                  locale,
                }),
              },
              tooltip: i18n.t('finance.deals.columns.estimated_amount'),
            },
            {
              icon: Suitcase,
              property: {
                type: 'company' as const,
                value: {
                  name: contactFullName(deal.client),
                },
              },
              tooltip: i18n.t('finance.deals.columns.client'),
            },
            {
              icon: Person,
              property: {
                type: 'person' as const,
                value: {
                  firstName: deal.owner?.firstName ?? '',
                  lastName: deal.owner?.lastName ?? '',
                  src: deal.owner?.avatar?.url ? buildUrl(deal.owner.avatar.url) : undefined,
                },
              },
              tooltip: i18n.t('finance.deals.columns.owner'),
            },
            {
              icon: Receipt,
              property: {
                type: 'number' as const,
                value:
                  (deal.invoicesConnection.totalCount ?? 0) +
                  (deal.quotesConnection.totalCount ?? 0),
              },
              tooltip: i18n.t('finance.sale_invoices.detail.documents'),
            },
            {
              icon: Calendar,
              property: {
                type: 'text' as const,
                value: deal.expectedCloseDate
                  ? convertDate(deal.expectedCloseDate, 'backend_datetime', 'ui_dmy_long')
                  : i18n.t('finance.deals.not_applicable'),
              },
              tooltip: i18n.t('finance.deals.columns.expected_close_date'),
            },
          ],
        },
      },
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns, handleMove, lanes, locale]
  )

  const collection = (
    <OneDataCollection source={source} visualizations={visualizations} fullHeight />
  )

  return (
    <Box
      ref={containerRef}
      style={availableHeight ? { height: `${availableHeight - 1}px` } : undefined}
    >
      {collection}
    </Box>
  )
}
